import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { RepricerArticlesApi } from "../../services/api/multi/repricer";
import {
  ArticleSalesStatsDialog,
  SalesStatsOpenButton,
} from "../MultiRepricer/components/ArticleSalesStatsDialog";
import {
  OTTO_AD_DATA_MAX_DAYS,
  SALES_STATS_ALL_DAYS,
  SALES_STATS_ALL_PERIOD,
  SALES_STATS_CUSTOM_PERIOD,
  SALES_STATS_DAY_OPTIONS,
  formatSalesStatsPeriodLabel,
} from "../MultiRepricer/hooks/useArticleSalesStats";
import { getFilterPanelSx } from "../../theme/panelStyles";
import AnalysisStockExpandPanel from "./AnalysisStockExpandPanel";

const PAGE_SIZES = [25, 50, 100];
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

function getCached(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCached(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // storage quota exceeded — ignore
  }
}

/** Article DB fields — applied via /articles filters. */
const ARTICLE_FILTER_FIELDS = new Set(["brutto_buy_price", "stock"]);
/** Period KPIs — applied server-side via /articles/analytics kpi_filters. */
const STATS_FILTER_FIELDS = new Set([
  "period_profit",
  "sales_qty",
  "retour_qty",
  "retour_rate",
  "gross_revenue",
  "total_ad_costs",
  "ad_spend_per_sale",
]);

const ADVANCED_FILTER_FIELDS = [
  { value: "brutto_buy_price", label: "Buy Price" },
  { value: "period_profit", label: "Period Profit" },
  { value: "stock", label: "Stock" },
  { value: "sales_qty", label: "Sales Qty" },
  { value: "retour_qty", label: "Returns Qty" },
  { value: "retour_rate", label: "Return Rate (%)" },
  { value: "gross_revenue", label: "Gross Revenue" },
  { value: "total_ad_costs", label: "Ad Costs" },
  { value: "ad_spend_per_sale", label: "Ad Cost / Sale" },
];

const money = (value) =>
  value == null
    ? "—"
    : `${Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })} €`;
const number = (value) =>
  value == null
    ? "—"
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

function toIsoDate(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  return value.toISOString().slice(0, 10);
}

function daysAgoIso(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (days - 1));
  return toIsoDate(d);
}

function todayIso() {
  return toIsoDate(new Date());
}

function adCostPerSale(article) {
  if (article?.ad_spend_per_sale != null && article.ad_spend_per_sale !== "") {
    return Number(article.ad_spend_per_sale);
  }
  const costs = Number(article?.total_ad_costs);
  const sales = Number(article?.total_ad_sales);
  if (Number.isFinite(costs) && Number.isFinite(sales) && sales > 0) {
    return costs / sales;
  }
  return null;
}

export default function AnalysisArticles() {
  const theme = useTheme();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortOrder, setSortOrder] = useState({ field: "sku", dir: "asc" });

  const [skuSearch, setSkuSearch] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [activeSkuSearch, setActiveSkuSearch] = useState("");
  const [activeNameSearch, setActiveNameSearch] = useState("");
  const [hasDetailsFilter, setHasDetailsFilter] = useState("ALL");
  const [articleTypeFilter, setArticleTypeFilter] = useState("ALL");
  const [stockRoleFilter, setStockRoleFilter] = useState("ALL");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [activeAdvancedFilters, setActiveAdvancedFilters] = useState([]);

  const [periodSelect, setPeriodSelect] = useState(30);
  const [customFrom, setCustomFrom] = useState(daysAgoIso(30));
  const [customTo, setCustomTo] = useState(todayIso());
  const [salesStatsSku, setSalesStatsSku] = useState(null);
  const [salesStatsName, setSalesStatsName] = useState(null);
  const [expandedSku, setExpandedSku] = useState(null);

  const statsWindow = useMemo(() => {
    if (periodSelect === SALES_STATS_ALL_PERIOD) {
      return { days: SALES_STATS_ALL_DAYS };
    }
    if (periodSelect === SALES_STATS_CUSTOM_PERIOD) {
      const from_date = customFrom || daysAgoIso(30);
      const to_date = customTo || todayIso();
      const start = new Date(`${from_date}T00:00:00`);
      const end = new Date(`${to_date}T00:00:00`);
      const span = Math.max(
        1,
        Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1,
      );
      return { from_date, to_date, days: span };
    }
    return { days: periodSelect };
  }, [periodSelect, customFrom, customTo]);

  const periodLabel = useMemo(
    () => formatSalesStatsPeriodLabel(statsWindow, periodSelect),
    [periodSelect, statsWindow],
  );

  const buildFilters = useCallback(() => {
    const f = [];
    if (activeSkuSearch)
      f.push({ field: "sku", op: "like", value: `%${activeSkuSearch}%` });
    if (activeNameSearch)
      f.push({
        field: "articlename",
        op: "like",
        value: `%${activeNameSearch}%`,
      });
    if (hasDetailsFilter !== "ALL")
      f.push({ field: `${hasDetailsFilter}.article_id`, op: "is_not_null" });
    if (articleTypeFilter !== "ALL")
      f.push({ field: "article_type", op: "eq", value: articleTypeFilter });
    if (stockRoleFilter !== "ALL")
      f.push({ field: "stock_role", op: "eq", value: stockRoleFilter });
    // Only article columns can be pushed to the articles API.
    for (const af of activeAdvancedFilters) {
      if (!ARTICLE_FILTER_FIELDS.has(af.field) || !af.op) continue;
      if (af.op === "is_null" || af.op === "is_not_null") {
        f.push({ field: af.field, op: af.op });
      } else if (af.value !== "") {
        f.push({ field: af.field, op: af.op, value: af.value });
      }
    }
    return f.length ? f : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSkuSearch,
    activeNameSearch,
    hasDetailsFilter,
    articleTypeFilter,
    stockRoleFilter,
    searchTrigger,
    activeAdvancedFilters,
  ]);

  const activeStatsFilters = useMemo(
    () =>
      activeAdvancedFilters.filter(
        (af) =>
          STATS_FILTER_FIELDS.has(af.field) &&
          af.op &&
          (af.op === "is_null" || af.op === "is_not_null" || af.value !== ""),
      ),
    [activeAdvancedFilters],
  );
  const loadArticles = useCallback(
    async (force = false) => {
      setError(null);
      try {
        if (
          periodSelect === SALES_STATS_CUSTOM_PERIOD &&
          statsWindow.from_date &&
          statsWindow.to_date &&
          statsWindow.from_date > statsWindow.to_date
        ) {
          throw new Error("From date must be on or before to date.");
        }
        if (
          periodSelect === SALES_STATS_CUSTOM_PERIOD &&
          statsWindow.days > 365
        ) {
          throw new Error("Custom window cannot exceed 365 days.");
        }
        // days=0 is all-time; skip the >365 check for that sentinel.
        if (
          periodSelect !== SALES_STATS_ALL_PERIOD &&
          periodSelect !== SALES_STATS_CUSTOM_PERIOD &&
          statsWindow.days > 365
        ) {
          throw new Error("Period cannot exceed 365 days.");
        }

        const filters = buildFilters();
        const orders = [{ field: sortOrder.field, direction: sortOrder.dir }];
        const cacheKey = `analysis_articles_v4_${JSON.stringify({
          filters,
          orders,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          statsWindow,
          kpiFilters: activeStatsFilters,
        })}`;
        if (!force) {
          const cached = getCached(cacheKey);
          if (cached) {
            setArticles(cached.articles || []);
            setTotal(cached.total || 0);
            return;
          }
        }

        setLoading(true);
        const response = await RepricerArticlesApi.getArticlesAnalytics({
          filters,
          kpi_filters: activeStatsFilters.length
            ? activeStatsFilters
            : undefined,
          orders,
          channel: "all",
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          ...statsWindow,
        });
        const payload = response.data || {};
        const items = payload.items || [];
        const nextTotal = Number(payload.total) || 0;
        setArticles(items);
        setTotal(nextTotal);
        setCached(cacheKey, { articles: items, total: nextTotal });
      } catch (requestError) {
        setArticles([]);
        setTotal(0);
        setError(
          requestError.response?.data?.detail ||
            requestError.message ||
            "Failed to load article analytics.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      buildFilters,
      page,
      periodSelect,
      rowsPerPage,
      sortOrder,
      statsWindow,
      activeStatsFilters,
    ],
  );

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleSearch = () => {
    setActiveSkuSearch(skuSearch.trim());
    setActiveNameSearch(nameSearch.trim());
    setActiveAdvancedFilters([...advancedFilters]);
    setPage(0);
    setSearchTrigger((n) => n + 1);
  };

  const handleClearSearch = () => {
    setSkuSearch("");
    setNameSearch("");
    setActiveSkuSearch("");
    setActiveNameSearch("");
    setHasDetailsFilter("ALL");
    setArticleTypeFilter("ALL");
    setStockRoleFilter("ALL");
    setAdvancedFilters([]);
    setActiveAdvancedFilters([]);
    setPage(0);
    setSearchTrigger((n) => n + 1);
  };

  const handleAddAdvancedFilter = () => {
    setAdvancedFilters((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length}`,
        field: "stock",
        op: "gte",
        value: "",
      },
    ]);
  };

  const handleRemoveAdvancedFilter = (id) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpdateAdvancedFilter = (id, key, val) => {
    setAdvancedFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: val } : f)),
    );
  };

  const handleSort = (field) => {
    setSortOrder((current) => ({
      field,
      dir: current.field === field && current.dir === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const header = (label, field, align = "left") => (
    <TableCell
      align={align}
      sortDirection={sortOrder.field === field ? sortOrder.dir : false}
    >
      <TableSortLabel
        active={sortOrder.field === field}
        direction={sortOrder.field === field ? sortOrder.dir : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const showOttoAdRangeWarning =
    periodSelect === SALES_STATS_ALL_PERIOD ||
    statsWindow.days > OTTO_AD_DATA_MAX_DAYS ||
    statsWindow.days === SALES_STATS_ALL_DAYS;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
          >
            <Box>
              <Typography variant="h6">Article analytics</Typography>
              <Typography variant="body2" color="text.secondary">
                Filter like Multi Repricer. Sorting and KPI filters run on the
                server across all matching articles for the selected period.
                Return rate filters use percent (e.g. 15 for 15%).
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadArticles(true)}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              size="small"
              placeholder="Search SKU"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: skuSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSkuSearch("");
                      setActiveSkuSearch("");
                      setPage(0);
                      setSearchTrigger((n) => n + 1);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                ),
              }}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              placeholder="Search Name"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: nameSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setNameSearch("");
                      setActiveNameSearch("");
                      setPage(0);
                      setSearchTrigger((n) => n + 1);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                ),
              }}
              sx={{ width: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Has Details</InputLabel>
              <Select
                value={hasDetailsFilter}
                label="Has Details"
                onChange={(e) => {
                  setHasDetailsFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="plenty">Plenty (excl. FBA)</MenuItem>
                <MenuItem value="shopify">Shopify</MenuItem>
                <MenuItem value="kaufland">Kaufland</MenuItem>
                <MenuItem value="otto">Otto</MenuItem>
                <MenuItem value="fbm">FBM</MenuItem>
                <MenuItem value="fba">FBA</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={stockRoleFilter}
                label="Type"
                onChange={(e) => {
                  setStockRoleFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="main">Main</MenuItem>
                <MenuItem value="bundle">Bundle (any)</MenuItem>
                <MenuItem value="bundle_shell">Bundle shell</MenuItem>
                <MenuItem value="bundle_item">Bundle item</MenuItem>
                <MenuItem value="single">Single (no bundle)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Article Type</InputLabel>
              <Select
                value={articleTypeFilter}
                label="Article Type"
                onChange={(e) => {
                  setArticleTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="penner">Penner</MenuItem>
                <MenuItem value="renner">Renner</MenuItem>
                <MenuItem value="neutral">Neutral</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="analysis-period">Period</InputLabel>
              <Select
                labelId="analysis-period"
                label="Period"
                value={periodSelect}
                onChange={(e) => {
                  const next = e.target.value;
                  setPeriodSelect(next);
                  if (
                    next !== SALES_STATS_CUSTOM_PERIOD &&
                    next !== SALES_STATS_ALL_PERIOD
                  ) {
                    setCustomFrom(daysAgoIso(Number(next)));
                    setCustomTo(todayIso());
                  }
                  setPage(0);
                }}
              >
                {SALES_STATS_DAY_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    Last {value} days
                  </MenuItem>
                ))}
                <MenuItem value={SALES_STATS_ALL_PERIOD}>All time</MenuItem>
                <MenuItem value={SALES_STATS_CUSTOM_PERIOD}>Custom…</MenuItem>
              </Select>
            </FormControl>
            {periodSelect === SALES_STATS_CUSTOM_PERIOD && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={customFrom}
                  onChange={(e) => {
                    setCustomFrom(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={customTo}
                  onChange={(e) => {
                    setCustomTo(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
              </>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
            <Button
              variant={advancedFiltersOpen ? "contained" : "outlined"}
              size="small"
              onClick={() => setAdvancedFiltersOpen((prev) => !prev)}
              startIcon={<FilterListIcon />}
            >
              Filters ({advancedFilters.length})
            </Button>
            {(activeSkuSearch ||
              activeNameSearch ||
              hasDetailsFilter !== "ALL" ||
              articleTypeFilter !== "ALL" ||
              stockRoleFilter !== "ALL" ||
              activeAdvancedFilters.length > 0) && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearSearch}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            )}
          </Stack>

          <Collapse in={advancedFiltersOpen} unmountOnExit>
            <Paper variant="outlined" sx={getFilterPanelSx(theme)}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="subtitle2">Advanced Filters</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdvancedFilter}
                >
                  Add Rule
                </Button>
              </Stack>
              {advancedFilters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No advanced filters active.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {advancedFilters.map((f) => (
                    <Stack
                      key={f.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={f.field}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "field",
                              e.target.value,
                            )
                          }
                        >
                          {ADVANCED_FILTER_FIELDS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={f.op}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "op",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="eq">Equals (=)</MenuItem>
                          <MenuItem value="gt">Greater (&gt;)</MenuItem>
                          <MenuItem value="gte">Greater eq (&gt;=)</MenuItem>
                          <MenuItem value="lt">Less (&lt;)</MenuItem>
                          <MenuItem value="lte">Less eq (&lt;=)</MenuItem>
                          <MenuItem value="like">Contains</MenuItem>
                          <MenuItem value="is_null">Is null/none</MenuItem>
                          <MenuItem value="is_not_null">Is not null</MenuItem>
                        </Select>
                      </FormControl>
                      {f.op !== "is_null" && f.op !== "is_not_null" && (
                        <TextField
                          size="small"
                          placeholder="Value"
                          value={f.value}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "value",
                              e.target.value,
                            )
                          }
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          sx={{ width: 140 }}
                        />
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdvancedFilter(f.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearch}
                      sx={{ mt: 1 }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Collapse>

          {showOttoAdRangeWarning && (
            <Alert severity="warning">
              Otto SPA ad data covers at most the last {OTTO_AD_DATA_MAX_DAYS}{" "}
              days. Sales/profit use the full window, but ad KPIs may be
              incomplete.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" width={48} />
                  {header("SKU", "sku")}
                  {header("Name", "articlename")}
                  <TableCell>Type</TableCell>
                  {header("Stock", "stock", "right")}
                  {header("Sales qty", "sales_qty", "right")}
                  {header("Return qty", "retour_qty", "right")}
                  {header("Return rate", "retour_rate", "right")}
                  {header("Period profit", "period_profit", "right")}
                  {header("Gross revenue", "gross_revenue", "right")}
                  {header("Ad costs (Otto)", "total_ad_costs", "right")}
                  {header("Ad cost / sale", "ad_spend_per_sale", "right")}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {articles.map((article) => {
                  const isExpanded = expandedSku === article.sku;
                  const isMain = Boolean(article.is_stock_supplier);
                  return (
                    <React.Fragment key={article.sku}>
                      <TableRow
                        hover
                        selected={isExpanded}
                        sx={{
                          "& > *": isExpanded
                            ? { borderBottom: "unset" }
                            : undefined,
                        }}
                      >
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            aria-label={
                              isExpanded ? "Collapse row" : "Expand row"
                            }
                            onClick={() =>
                              setExpandedSku(isExpanded ? null : article.sku)
                            }
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              {article.sku}
                            </Typography>
                            {article.is_one_to_one_bundle &&
                              (article.one_to_one_linked_skus || []).length >
                                0 && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  {(article.one_to_one_linked_skus || []).map(
                                    (linkedSku) => (
                                      <Chip
                                        key={linkedSku}
                                        size="small"
                                        label={`1:1 ${linkedSku}`}
                                        variant="outlined"
                                        sx={{ maxWidth: 280 }}
                                      />
                                    ),
                                  )}
                                </Stack>
                              )}
                            {article.is_one_to_one_bundle &&
                              !(article.one_to_one_linked_skus || []).length &&
                              article.one_to_one_component_sku && (
                                <Chip
                                  size="small"
                                  label={`1:1 ${article.one_to_one_component_sku}`}
                                  variant="outlined"
                                  sx={{
                                    alignSelf: "flex-start",
                                    maxWidth: 280,
                                  }}
                                />
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell>{article.articlename || "—"}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {isMain ? (
                              <Chip
                                size="small"
                                label="Main"
                                color="info"
                                variant="outlined"
                              />
                            ) : null}
                            {article.bundle_type === "bundle" ? (
                              <Chip
                                size="small"
                                label="Bundle"
                                color="secondary"
                                variant="outlined"
                              />
                            ) : null}
                            {article.bundle_type === "bundle_item" ? (
                              <Chip
                                size="small"
                                label="Bundle item"
                                color="default"
                                variant="outlined"
                              />
                            ) : null}
                            {!isMain && !article.bundle_type ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {number(article.stock)}
                        </TableCell>
                        <TableCell align="right">
                          {number(article.sales_qty)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          {number(article.retour_qty)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          {article.retour_rate == null
                            ? "—"
                            : `${(Number(article.retour_rate) * 100).toFixed(1)}%`}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: "success.main", fontWeight: 600 }}
                        >
                          {isMain ? (
                            <Tooltip
                              title={`Combined profit (single + bundle share). Raw Main SKU lines only: ${money(article.raw_main_period_profit ?? article.period_profit)}`}
                            >
                              <span>
                                {money(
                                  article.combined_period_profit ??
                                    article.period_profit,
                                )}
                              </span>
                            </Tooltip>
                          ) : (
                            money(article.period_profit)
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {money(article.gross_revenue)}
                        </TableCell>
                        <TableCell align="right">
                          {money(article.total_ad_costs)}
                        </TableCell>
                        <TableCell align="right">
                          {money(adCostPerSale(article))}
                        </TableCell>
                        <TableCell align="center">
                          <SalesStatsOpenButton
                            onClick={() => {
                              setSalesStatsSku(article.sku);
                              setSalesStatsName(article.articlename || null);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={13}
                          sx={{ py: 0, border: isExpanded ? undefined : 0 }}
                        >
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <AnalysisStockExpandPanel
                              sku={article.sku}
                              days={statsWindow.days}
                              channel="all"
                              fromDate={statsWindow.from_date}
                              toDate={statsWindow.to_date}
                              periodLabel={periodLabel}
                              onOpenSalesStats={({
                                sku: targetSku,
                                articlename,
                              }) => {
                                setSalesStatsSku(targetSku);
                                setSalesStatsName(articlename || null);
                              }}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                {!loading && articles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      No articles found.
                    </TableCell>
                  </TableRow>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      Loading analytics…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZES}
          />
        </Stack>
      </CardContent>
      <ArticleSalesStatsDialog
        open={Boolean(salesStatsSku)}
        onClose={() => {
          setSalesStatsSku(null);
          setSalesStatsName(null);
        }}
        sku={salesStatsSku}
        articlename={
          salesStatsName ||
          articles.find((article) => article.sku === salesStatsSku)?.articlename
        }
        defaultDays={statsWindow.days}
        defaultFromDate={statsWindow.from_date}
        defaultToDate={statsWindow.to_date}
      />
    </Card>
  );
}
